/**
 * useInstantPlayback - Sub-second time-to-first-play composable
 *
 * Implements the "instant playback" architecture from
 * `new_vision/INSTANT_PLAYBACK_SPEC.md` in the dashboard repo.
 *
 * Critical-path cold start:
 *   1. Read user's `last_completed_lego_id` (or accept an override).
 *   2. Fetch the course's `round-map` (one tiny query, cached forever
 *      per course version).
 *   3. Fetch ONE cycle from `/api/courses/:code/cycles?from=:legoId&limit=1`.
 *   4. Hand it back to the caller — audio fetch is the existing audio
 *      controller's job, not ours.
 *
 * Prefetch tiers run during playback (never block):
 *   - Tier 1: rest of the current round's cycles (limit=BOOTSTRAP_LIMIT)
 *   - Tier 3: next round's cycles
 *
 * Listening/presentation audio is NOT bulk-prefetched here (the old
 * Tier 2 was disabled 2026-05-23) — it JIT-fetches at use time and
 * rides `audioCache.persistent.ensure` + the service-worker
 * `CacheFirst` strategy, with `SimplePlayer.prefetchNextCycle`
 * warming the upcoming cycle's voices during the prompt/pause window.
 *
 * Coexists with `usePrefetchManager` (30-min audio buffer). The
 * critical path *replaces* the upfront whole-course assembly when the
 * feature flag is on; the legacy path stays in tree as the flag-off
 * fallback.
 *
 * The composable never touches the Audio element directly — the
 * existing `RealAudioController` plays cycles, we just provide the
 * cycle metadata.
 */

import { ref, computed, type Ref, type ComputedRef } from 'vue'
import { bootFetch } from '../playback/signedInBoot'
import {
  CRITICAL_PATH_TIMEOUT_MS,
  criticalPathBudgetMs,
  BUNDLE_BOOT_BUDGET_MS,
  BACKGROUND_FETCH_TIMEOUT_MS,
  markNetworkStalled,
  clearNetworkStalled,
  isOfflineish,
} from '../config/networkGate'
import { getCourseBundle, hasLocalCourseBundle, hasSessionCourseBundle } from './useCourseBundle'
import { firstSound, firstSoundFiredBy } from '../playback/firstSoundSignal'
import { isContentBlackoutActive } from '../playback/contentBlackout'
import { reportBundlePath, type BundlePathStage } from '../playback/bundlePathTelemetry'
import type { CourseBundle } from '@ssi/core'
import type { NextLegoFetchOutcome } from '../playback/practisingMode'
import {
  bundleToCyclesResponse,
  bundleToInfPlayCyclesResponse,
  bundleToRoundMap,
} from '../providers/bundleToBackendCycles'

// ============================================================================
// TYPES
// ============================================================================

/**
 * Cycle shape returned by the new backend `cycles` endpoint.
 *
 * This is the wire format from `GET /api/courses/:code/cycles`. It is
 * intentionally distinct from `types/Cycle.ts`'s atomic playback
 * `Cycle` (which is bound to a single PROMPT/PAUSE/VOICE_1/VOICE_2
 * pair). The backend cycle is one element in the round's sequence —
 * intro, component_intro, debut, build, use, or listening hook — and the
 * player layer is responsible for converting it into one or more atomic
 * playback cycles.
 */
export interface BackendCycle {
  id: string
  /** `component_intro` = the per-piece "as in" narration for one component
   *  of an M-LEGO. Emitted since 2026-08-04; treated as an intro variant by
   *  `toPlayerCycle` (presentation audio in the prompt slot, no pause). */
  type: 'intro' | 'component_intro' | 'debut' | 'build' | 'spaced_rep' | 'use' | 'listening'
  /** The LEGO this cycle is ABOUT. For a `spaced_rep` cycle that is the
   *  EARLIER LEGO being reviewed, not the round it plays in — see
   *  `round_lego_id`. */
  lego_id: string
  /** The LEGO whose ROUND this cycle belongs to. Set only on cross-LEGO
   *  cycles (spaced review), where it differs from `lego_id`; absent
   *  everywhere else, so `round_lego_id ?? lego_id` is always the round key.
   *  This is the buffer/pagination key — `next_lego_id` names a round LEGO. */
  round_lego_id?: string
  /** Index into the endpoint's Fibonacci offset list — 0 is N-1. spaced_rep only. */
  fib_position?: number
  /** Round number of the LEGO being reviewed. spaced_rep only. */
  review_of?: number
  /** INF PLAY round this cycle belongs to (1-based past the main loop).
   *  Set by /infplay-cycles and by the bundle path's infplay adapter;
   *  `bootstrapInfPlay` groups on it to synthesise a round map. */
  inf_round?: number
  seed_number: number
  known_text: string
  target_text: string
  target_text_native?: string
  components?: Array<{ known: string; target: string }>
  /** The intro's AUTHORED word mapping: literal known-language chunks in the
   *  TARGET's own word order, each covering `span` consecutive target words.
   *  Emitted by the cycles endpoint on intro/debut cycles when a human has
   *  segmented the row in Popty. PREFERRED over `components` when present —
   *  componentisation stays the fallback (Tom, 2026-08-13). */
  gloss_segments?: Array<{ span: number; known: string }>
  decomposition?: Array<{
    legoId: string | null
    target: string
    known: string
    isGhost: boolean
  }>
  /** Authored display tiles ({n: native, r: roman, salient}) from
   * course_practice_phrases.display_tiling — rendered verbatim by the player
   * when present (native primary, roman ruby); absent → runtime segmenter. */
  display_tiling?: Array<{ n: string; r: string; salient?: boolean }>
  audio: {
    known_id?: string
    target1_id?: string
    target2_id?: string
    presentation_id?: string
  }
  durations: {
    target1_ms?: number
    target2_ms?: number
  }
  is_new: boolean
}

export interface RoundMapEntry {
  /** Round index (1-based per spec, matches `course_round_index.round_index`) */
  r: number
  legoId: string
  seed: number
}

export interface RoundMap {
  course_code: string
  /** Bumped on any LEGO/decomposition change — used for cache busting */
  version: number
  rounds: RoundMapEntry[]
}

export interface CyclesResponse {
  course_code: string
  version: number
  cycles: BackendCycle[]
  next_lego_id: string | null
}

export interface BootstrapResult {
  firstCycle: BackendCycle
  mapVersion: number
}

export interface UseInstantPlaybackOptions {
  /**
   * Resolver for the learner's current position — `last_completed_lego_id`
   * from the enrollment row (or `null` for a fresh start). Caller wires
   * this to whichever store owns position (usually the
   * `progressStore.getEnrollment` path used by `LearningPlayer.vue`).
   *
   * If the caller passes an explicit `legoId` to `bootstrap()`, the
   * resolver is bypassed.
   */
  resolveStartLegoId?: () => Promise<string | null> | string | null

  /** Override the default `/api/courses` base — useful for testing. */
  apiBase?: string
}

// ============================================================================
// CONSTANTS
// ============================================================================

// ============================================================================
// BUNDLE CUTOVER (design step 5) — per-course flag
// ============================================================================
//
// When a course is bundle-enabled, `fetchRoundMap` and `fetchCycles` stop
// hitting `/round-map` and `/cycles` and compute the SAME wire payloads from
// one cached `/bundle` fetch via the unified generator
// (`providers/bundleToBackendCycles`). Everything downstream — the cycle
// buffer, partial-LEGO bookkeeping, pagination, `backendCyclesToRounds` — is
// untouched, because the shapes are identical. Proven cycle-by-cycle against
// the live endpoint by `tools/bundle-cutover/parity-cycles.mjs --wire`.
//
// Reverts by flag, not by revert commit; and ANY failure on the bundle path
// falls through to the network path, so the worst case is today's behaviour.
//
// `?bundle=1` / `?bundle=0` override per session for dev testing, alongside
// the existing `?fc=1` / `?stream` / `?reset=1` cheats.
const BUNDLE_BOOTSTRAP_ALL = false
/**
 * Consulted only when BUNDLE_BOOTSTRAP_ALL is false.
 *
 * A course is added ONLY after it has passed both harnesses against the live
 * endpoints — `parity-cycles.mjs` (generator and wire modes, three positions,
 * walked through the client's own paging loop) and `parity-infplay.mjs`
 * (three entry rounds, both producers sampled). Results are committed under
 * `docs/bundle-cutover-parity/`. A course that has not been walked is not on
 * this list, whatever it resembles.
 *
 * Batch 1 (2026-08-29, free) — hun_for_eng had already shipped; the other
 * eight joined it on the same evidence run.
 * Batch 2 (2026-08-29, premium) — walked twice: once with an entitled session
 * for the full course, and once anonymously for the 19-seed free preview an
 * unsubscribed visitor actually gets. Both byte-identical.
 *
 * NOT on the list, and why: fin_for_eng. It has 1,394 rounds and no rendered
 * audio at all, so both paths emit nothing and parity proves nothing about it.
 * A vacuous pass is not a pass.
 */
const BUNDLE_BOOTSTRAP_COURSES = new Set<string>([
  // batch 1 — free
  'hun_for_eng',
  'gle_for_eng',
  'nld_for_eng',
  'tur_for_eng',
  'eus_for_eng',
  'pol_for_eng',
  'heb_for_eng',
  'tha_for_eng',
  'hin_for_eng',
  // batch 2 — premium
  'spa_for_eng',
  'fra_for_eng',
  'jpn_for_eng',
  'zho_for_eng',
  'cym_s_for_eng',
  'zho_for_gle',
])

export function isBundleBootstrapEnabled(courseCode: string): boolean {
  try {
    const override = new URLSearchParams(window.location.search).get('bundle')
    if (override === '1') return true
    if (override === '0') return false
  } catch {
    /* no window (tests / SSR) — fall through to the static flags */
  }
  return BUNDLE_BOOTSTRAP_ALL || BUNDLE_BOOTSTRAP_COURSES.has(courseCode)
}

const ROUND_MAP_STORAGE_PREFIX = 'ssi-instant-playback-roundmap-'
const CYCLES_STORAGE_PREFIX = 'ssi-instant-playback-cycles-'

/**
 * Bootstrap fetches the whole first round in one shot (limit≈15) so
 * SimplePlayer can be initialised with a complete round structure and
 * audio can start playing immediately. The spec originally proposed
 * limit=1 + tier-1-during-playback, but appending cycles to an
 * already-running round adds engine complexity for ~zero latency win
 * (the cycles endpoint costs roughly the same for limit=1 vs a full round
 * once the Lambda is warm — the payload is tiny either way). Cheaper
 * × simpler to grab the full round at bootstrap.
 */
/**
 * 25, not 15, since 2026-08-09. A round is now the walk's full shape —
 * intro + debut + BUILD ×≤7 + spaced_rep ×≤12 + USE ×≤2 = at most 23 cycles.
 * At 15 the endpoint would cut every round in half, `next_lego_id` would point
 * back at the same LEGO on every fetch, `isLegoComplete` would never clear the
 * partial flag, and `backendCyclesToRounds` would emit NO round at all. The
 * limit has to clear a whole round for the "grab the full round at bootstrap"
 * design above to hold.
 */
const BOOTSTRAP_LIMIT = 25
/** Tier 1: rest of the current round. Kept for backwards compatibility but bootstrap now covers it. */
const TIER_1_LIMIT = 25
const SECOND_ROUND_SETTLE_MS = 20_000
/** Tier 3: next round. Same limit as tier 1. */
const TIER_3_LIMIT = 25
/** INF PLAY's limit counts ROUNDS, not cycles, and its endpoint caps at 15 —
 *  a different unit from the three above, so it gets its own constant rather
 *  than riding a main-loop cycle count that happens to clamp to the same
 *  number. */
const INFPLAY_ROUND_LIMIT = 15
/**
 * INF PLAY's first window (job #290): two rounds, ~44 cycles, is minutes of
 * play. The other thirteen follow at once from prefetchNextInfPlayBatch, which
 * the player fires the moment the bootstrap returns, so nothing is lost but
 * the wait: a 15-round answer was ~330 cycles to build, stamp and send in
 * front of the first sound.
 */
const INFPLAY_BOOT_ROUNDS = 2

// ============================================================================
// ROUND-MAP CACHE (localStorage, version-stamped)
// ============================================================================

interface CachedRoundMap {
  map: RoundMap
  cachedAt: number
}

function readCachedRoundMap(courseCode: string): RoundMap | null {
  try {
    const raw = localStorage.getItem(ROUND_MAP_STORAGE_PREFIX + courseCode)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedRoundMap
    if (parsed?.map?.course_code === courseCode && Array.isArray(parsed.map.rounds)) {
      return parsed.map
    }
    return null
  } catch (err) {
    console.warn('[InstantPlayback] Failed to read cached round-map:', err)
    return null
  }
}

function writeCachedRoundMap(courseCode: string, map: RoundMap): void {
  try {
    const payload: CachedRoundMap = { map, cachedAt: Date.now() }
    localStorage.setItem(
      ROUND_MAP_STORAGE_PREFIX + courseCode,
      JSON.stringify(payload),
    )
  } catch (err) {
    console.warn('[InstantPlayback] Failed to write round-map cache:', err)
  }
}

// ============================================================================
// CYCLES CACHE (localStorage, version-stamped)
// ============================================================================
//
// Caches the cycles response keyed by {course, fromLegoId}. Stamped with the
// course version observed at write time — readers compare against the current
// round-map's version and evict on mismatch, so a content edit that bumps
// courses.version invalidates all cached cycle responses for that course on
// the next fetch.
//
// Why we do this: legacy's useScriptCache made returning visitors feel
// instant because the script lived in localStorage. The new instant-playback
// path caches the round-map but, without this cache, paid the full cycles
// network round-trip every visit. Caching the cycles too makes "open a
// course I was just learning" a zero-network operation until content changes.

interface CachedCycles {
  response: CyclesResponse
  cachedAt: number
}

function cyclesCacheKey(courseCode: string, fromLegoId: string): string {
  return CYCLES_STORAGE_PREFIX + courseCode + '-' + fromLegoId
}

function readCachedCycles(
  courseCode: string,
  fromLegoId: string,
  expectedVersion: number,
): CyclesResponse | null {
  try {
    const raw = localStorage.getItem(cyclesCacheKey(courseCode, fromLegoId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedCycles
    if (
      parsed?.response?.course_code === courseCode &&
      Array.isArray(parsed.response.cycles) &&
      parsed.response.version === expectedVersion
    ) {
      return parsed.response
    }
    return null
  } catch (err) {
    console.warn('[InstantPlayback] Failed to read cycles cache:', err)
    return null
  }
}

/**
 * Read cached cycles IGNORING the version stamp and the requested limit.
 *
 * `readCachedCycles` is the correctness-first reader: it refuses a stale
 * vintage so a content edit reaches the learner. This one is the
 * last-resort reader, used only when the network has already failed or timed
 * out. At that moment the choice is not "stale content vs fresh content", it
 * is "stale content vs silence" — and Tom's ruling settles that: play what you
 * have. The stale copy is served for this session; the next successful fetch
 * overwrites it. (Tom 2026-08-15.)
 */
/**
 * Does this device hold ANYTHING to play for this course if the network is
 * slow? The round-map is cached on the first successful fast-path boot, so
 * its absence means this device has never booted this course that way.
 */
function hasLocalCourseFallback(courseCode: string): boolean {
  return readCachedRoundMap(courseCode) !== null
}

function readAnyCachedCycles(
  courseCode: string,
  fromLegoId: string,
): CyclesResponse | null {
  try {
    const raw = localStorage.getItem(cyclesCacheKey(courseCode, fromLegoId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedCycles
    const r = parsed?.response
    if (r?.course_code === courseCode && Array.isArray(r.cycles) && r.cycles.length > 0) {
      return r
    }
    return null
  } catch {
    return null
  }
}

function writeCachedCycles(
  courseCode: string,
  fromLegoId: string,
  response: CyclesResponse,
): void {
  try {
    const payload: CachedCycles = { response, cachedAt: Date.now() }
    localStorage.setItem(
      cyclesCacheKey(courseCode, fromLegoId),
      JSON.stringify(payload),
    )
  } catch (err) {
    // localStorage may be full (rare — ~5MB budget, our payloads are ~5-10KB).
    // Swallow — cache miss next time is fine; no functional impact.
    console.warn('[InstantPlayback] Failed to write cycles cache:', err)
  }
}

// ============================================================================
// IN-FLIGHT REQUEST COALESCING (round-map / cycles cold fetches)
// ============================================================================
//
// prewarmInstantCaches (fire-and-forget on course select), the player-mount
// bootstrap, the resume resolver's getOrFetchRoundMap, and the post-cache-hit
// revalidate all fire the SAME round-map / cycles GETs. On a COLD course switch
// they race each other — every one misses the not-yet-written localStorage
// cache and fetches cold — so one round-map + one cycles fetch became ×3 each.
// Prewarm was ADDING load instead of preventing it.
//
// This coalesces concurrent identical GETs by URL: the first caller starts the
// fetch, later callers within its in-flight window share the SAME promise, and
// the entry clears on settle — so a genuinely-later refetch (e.g. an intentional
// post-first-paint revalidate once the first has already landed) still hits the
// network. The shared promise resolves to ALREADY-PARSED JSON, so the response
// body is never shared raw and there is no "body already read" hazard.

/**
 * The SHARED fetch's own leak-guard. A fire-and-forget prewarm has no caller
 * waiting on it, so it keeps the long leash — the point of this bound is that
 * the request cannot live forever, not that anyone is being made to wait.
 *
 * The learner's actual wait is a SEPARATE, much shorter budget, applied
 * per-caller at `makeAbort(CRITICAL_PATH_TIMEOUT_MS)` in bootstrap(). The
 * coalescer already supports this split: a caller's signal detaches that
 * caller from the shared promise without aborting the request other callers
 * still depend on. So bootstrap gives up on the network at 2.5s and serves
 * cache, while the request it started keeps running and warms the cache for
 * the next call. (Tom 2026-08-15: "never allow a weak Internet connection to
 * block the learner".)
 */
const BOOT_FETCH_TIMEOUT_MS = BACKGROUND_FETCH_TIMEOUT_MS

interface CoalescedJson {
  ok: boolean
  status: number
  statusText: string
  data: unknown
}

// ---------------------------------------------------------------------------
// AUTH TOKEN for the fast-path content fetches (round-map / cycles / infplay).
// ---------------------------------------------------------------------------
// The /cycles + /infplay-cycles endpoints are entitlement-gated server-side
// (d4396730): a PREMIUM course past the free-preview window (seed <=19) returns
// 403 to any caller the server can't see a valid session for. These fetches
// used to go out as bare GETs with NO Authorization header, so a SIGNED-IN
// paid/admin learner past seed 19 was treated as anonymous → 403 → the client
// silently fell back to the slow legacy full-course walk. Invisible to guests
// (they never get past seed 19), which is why guest cold-switch benchmarks
// looked fine. Attaching the caller's Supabase access token makes the gate
// authorise paid users and the fast path holds.
//
// The provider is a module-level getter set once at app init (App.vue, after
// the Supabase client exists). getSession() reads the token from local storage
// with no network round-trip, so calling it per fetch is cheap. Null provider
// or null token → the fetch goes out anonymous exactly as before (correct for
// guests and free courses).
let authTokenProvider: (() => Promise<string | null>) | null = null

export function setInstantPlaybackAuthProvider(
  fn: (() => Promise<string | null>) | null,
): void {
  authTokenProvider = fn
}

async function authHeaders(): Promise<Record<string, string> | undefined> {
  if (!authTokenProvider) return undefined
  try {
    const token = await authTokenProvider()
    return token ? { Authorization: `Bearer ${token}` } : undefined
  } catch {
    return undefined
  }
}

const inflightGets = new Map<string, Promise<CoalescedJson>>()

/** AbortError shaped like a real fetch abort, so existing
 *  `(err).name === 'AbortError'` guards keep working in any JS env
 *  (DOMException isn't guaranteed outside the browser). */
function abortError(): Error {
  const e = new Error('Aborted')
  e.name = 'AbortError'
  return e
}

function sharedJsonGet(url: string): Promise<CoalescedJson> {
  const existing = inflightGets.get(url)
  if (existing) return existing
  const p = (async (): Promise<CoalescedJson> => {
    // The shared fetch owns its own timeout so a fire-and-forget prewarm (no
    // caller signal) can't leak forever; per-caller early-abort is layered on
    // top in coalescedJsonGet without touching this shared request.
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), BOOT_FETCH_TIMEOUT_MS)
    try {
      const headers = await authHeaders()
      // Through the signed-in boot (job #298): its first window answers the
      // round map and the resume window's cycles under these exact URLs, a
      // second before this code could have asked. Anything else, the network.
      const res = await bootFetch(url, headers ? { signal: ctrl.signal, headers } : { signal: ctrl.signal })
      if (!res.ok) {
        // LOUD on entitlement denial — a signed-in paid user hitting 403 here
        // means the fast path is about to silently degrade to the slow legacy
        // walk. This must never pass unnoticed again (regression: d4396730 shipped
        // the gate; the client wasn't sending the token). Callers also surface
        // this to telemetry via the `.status` on the thrown error.
        if (res.status === 403) {
          console.error(
            `[InstantPlayback] 403 on ${url} — entitlement gate denied the fast path. ` +
            `If the learner is signed-in and paid, the auth token is not reaching the server; ` +
            `playback will degrade to the slow legacy walk.`,
          )
        }
        return { ok: false, status: res.status, statusText: res.statusText, data: null }
      }
      return { ok: true, status: res.status, statusText: res.statusText, data: await res.json() }
    } finally {
      clearTimeout(timer)
    }
  })()
  inflightGets.set(url, p)
  const clear = () => { inflightGets.delete(url) }
  p.then(clear, clear)
  return p
}

/**
 * Coalesced JSON GET. Concurrent identical URLs share one fetch (see above).
 * An optional caller `signal` lets a specific caller stop awaiting (its boot
 * timeout, or cancel() on course-switch/unmount) without aborting the shared
 * fetch that other coalesced callers — or a fire-and-forget prewarm — still
 * depend on. The caller's promise rejects with an AbortError, matching the
 * pre-coalescing behaviour every call site already handles.
 */
function coalescedJsonGet(url: string, signal?: AbortSignal): Promise<CoalescedJson> {
  const shared = sharedJsonGet(url)
  if (!signal) return shared
  if (signal.aborted) return Promise.reject(abortError())
  return new Promise<CoalescedJson>((resolve, reject) => {
    const onAbort = () => reject(abortError())
    signal.addEventListener('abort', onAbort, { once: true })
    shared.then(
      (v) => { signal.removeEventListener('abort', onAbort); resolve(v) },
      (e) => { signal.removeEventListener('abort', onAbort); reject(e) },
    )
  })
}

// ============================================================================
// PREWARM (course switch / hover / idle)
// ============================================================================

/**
 * Warm the PROMPT clip of the intro cycle in a freshly-fetched batch.
 *
 * The intro's prompt is the presentation narration — the longest clip in the
 * course and the only class never pre-warmed before 2026-08-04. Resolution
 * mirrors `toPlayerCycle`: `presentation_id || known_id`, so a LEGO missing
 * its narration warms the clip that will actually be used instead of nothing.
 *
 * Deliberately ONE clip: the target voices are short and ride
 * `SimplePlayer.prefetchNextCycle` comfortably. Fire-and-forget — a prefetch
 * failure must never surface, let alone interrupt playback.
 */
function warmIntroPrompt(cycles: BackendCycle[] | undefined): void {
  const intro = cycles?.find((c) => c.type === 'intro')
  const id = intro?.audio?.presentation_id || intro?.audio?.known_id
  if (!id) return
  try {
    void fetch(`/api/audio/${encodeURIComponent(id)}`).catch(() => {})
  } catch {
    /* never let a prefetch escape */
  }
}

/**
 * Warm the instant-playback localStorage caches for a course WITHOUT mounting
 * the player, so the next mount's bootstrap() is a cache hit instead of two
 * cold serial network round-trips. Call on course switch (before the player
 * remounts), course-card hover, or idle. Reuses the EXACT cache keys the
 * composable reads. Fire-and-forget; silent on failure; skips work already
 * cached. round-map is position-independent (helps any switch); the
 * first-round cycles specifically de-cold the fresh-learner case (no saved
 * position → bootstrap starts at rounds[0]), which is the slow course-switch.
 */
/**
 * Warm the first cycle's clips in the order they play: the first clip alone,
 * to the last byte, then the rest together (job #784, the #773 first-sound
 * gate order). Four clips started at once split a slow pipe four ways, so the
 * one the learner hears first arrived last-equal instead of first. Plain GETs
 * of the same URL the player plays, so the HTTP cache answers the player.
 */
export async function warmClipsFirstClipFirst(ids: Array<string | null | undefined>): Promise<void> {
  const [head, ...rest] = ids.filter((id): id is string => !!id)
  if (!head) return
  const get = (id: string) => fetch(`/api/audio/${encodeURIComponent(id)}`)
  await get(head).then((r) => r.arrayBuffer()).catch(() => {})
  for (const id of rest) void get(id).catch(() => {})
}

/**
 * Warm several groups of clips one group after another, each through
 * `warmClipsFirstClipFirst`, and never twice for the same id (job #926). The
 * previous round's start and end used to be fired together, six-odd GETs
 * splitting a slow pipe with everything else in flight.
 */
export async function warmClipsInOrder(groups: Array<Array<string | null | undefined>>): Promise<void> {
  const seen = new Set<string>()
  for (const group of groups) {
    const fresh = group.filter((id): id is string => {
      if (!id || seen.has(id)) return false
      seen.add(id)
      return true
    })
    await warmClipsFirstClipFirst(fresh)
  }
}

export async function prewarmInstantCaches(
  courseCode: string,
  apiBase = '/api/courses',
): Promise<void> {
  if (!courseCode) return

  // Bundle cutover: on a bundle-enabled course the thing worth warming is the
  // BUNDLE — one fetch that then answers every round-map and cycles question
  // locally. Warming the old endpoints here instead would leave a flagged
  // course still calling /round-map and /cycles once each per session, which
  // is exactly what the cutover exists to remove. Fire-and-forget, same as
  // the rest of this function: a failure just means the next mount fetches.
  // Job #287: with no copy on the device the bundle is a whole-course
  // download, and it waits for the first sound (see bundleWithinBootBudget).
  if (isBundleBootstrapEnabled(courseCode) && (await hasLocalCourseBundle(courseCode).catch(() => false))) {
    try {
      const bundle = await getCourseBundle(courseCode)
      const map = bundleToRoundMap(bundle)
      writeCachedRoundMap(courseCode, map)
      // ...and warm the FIRST CYCLE'S AUDIO, exactly as the legacy branch
      // below does. The cutover quietly dropped this: a flagged course took
      // the early `return` above and never pre-fetched the clips the learner
      // actually hears, so a course switch paid a cold audio round-trip at
      // the moment of the tap that the old path had already paid ahead of
      // the remount. The cycles come out of the bundle in memory — no extra
      // network for the metadata, only the clips.
      const first = map.rounds?.[0]
      if (first) {
        const c0 = bundleToCyclesResponse(bundle, first.legoId, BOOTSTRAP_LIMIT).cycles?.[0]
        void warmClipsFirstClipFirst([
          c0?.audio?.presentation_id,
          c0?.audio?.known_id,
          c0?.audio?.target1_id,
          c0?.audio?.target2_id,
        ])
      }
    } catch {
      /* never let a prewarm escape */
    }
    return
  }

  try {
    let map = readCachedRoundMap(courseCode)
    if (!map) {
      const res = await coalescedJsonGet(`${apiBase}/${encodeURIComponent(courseCode)}/round-map`)
      if (!res.ok) return
      map = res.data as RoundMap
      writeCachedRoundMap(courseCode, map)
    }
    const first = map.rounds?.[0]
    if (!first) return
    // A signed-in learner resumes at their own position, which is rarely the
    // course's first round. Warming round 1's cycles and clips for them only
    // put the wrong round's audio in front of the right one on the pipe
    // (job #287, Portuguese). The round map above is all they need warmed.
    if (await authHeaders()) return
    let cycles = readCachedCycles(courseCode, first.legoId, map.version)
    if (!cycles) {
      const res = await coalescedJsonGet(
        `${apiBase}/${encodeURIComponent(courseCode)}/cycles?from=${encodeURIComponent(first.legoId)}&limit=${BOOTSTRAP_LIMIT}`,
      )
      if (!res.ok) return
      cycles = res.data as CyclesResponse
      writeCachedCycles(courseCode, first.legoId, cycles)
    }
    // Precache the FIRST cycle's audio into the SW CacheFirst layer so
    // warmFirstKnownAudio (the ready gate) and the opening PROMPT/VOICE plays
    // hit cache instead of streaming — the dominant cold-switch cost. Plain
    // GETs (same URL shape warmFirstKnownAudio uses), fire-and-forget.
    //
    // `presentation_id` FIRST and it is not optional: cycle 0 is always the
    // intro, whose PROMPT is the presentation narration — and intro cycles
    // carry no known audio when a presentation exists, so warming
    // known/target1/target2 alone warmed everything EXCEPT the clip that
    // actually plays first. Presentation is also the longest clip in the
    // course (~5.8s vs ~1.3s for the others), so it is simultaneously the
    // coldest and the most expensive thing to fetch late. A genuinely cold
    // Italian start measured 10.5s prompt→voice1 against 6.0s warm
    // (2026-08-04) — right at SimplePlayer's 10s stall watchdog, which drops
    // the phase and skips the intro for the rest of the session.
    const c0 = cycles.cycles?.[0]
    void warmClipsFirstClipFirst([
      c0?.audio?.presentation_id,
      c0?.audio?.known_id,
      c0?.audio?.target1_id,
      c0?.audio?.target2_id,
    ])
  } catch {
    /* best-effort prewarm — a cold mount just pays the round-trips as before */
  }
}

// ============================================================================
// COMPOSABLE
// ============================================================================

export function useInstantPlayback(
  courseCode: Ref<string>,
  options: UseInstantPlaybackOptions = {},
) {
  const apiBase = options.apiBase ?? '/api/courses'
  const resolveStartLegoId = options.resolveStartLegoId

  // -----------------------------------------------------------
  // State
  // -----------------------------------------------------------

  const roundMap = ref<RoundMap | null>(null)
  const isReady = ref(false)
  /** Last legoId returned/observed — drives `currentRound` lookup. */
  const currentLegoId = ref<string | null>(null)

  /**
   * Cycle buffer — Map<legoId, BackendCycle[]>. Populated by tier 1 + 3
   * prefetches. The first entry for a given lego is the introduction,
   * the last is the listening hook (per spec ordering).
   */
  const cycleBuffer = ref<Map<string, BackendCycle[]>>(new Map())

  /**
   * AbortController per active fetch — letting `cancel()` walk and
   * abort everything when the user navigates away.
   */
  const activeAborts = new Set<AbortController>()
  // Set by the first boot stage that stops waiting for the course bundle (job #926).
  let bundleGivenUpForBoot = false
  /** Courses whose bundle download waits for the first sound (job #287). */
  const bundleAfterFirstSound = new Set<string>()
  const abortTimers = new WeakMap<AbortController, ReturnType<typeof setTimeout>>()

  // The round-map / cycles fetches below are the boot-critical path
  // (bootstrap() awaits them directly from LearningPlayer's onMounted).
  // None of these fetch() calls carried a timeout — on a flaky mobile
  // connection they hang unboundedly, which is what stretched cold_start
  // telemetry's tail to 10s-133s+. Bounding at makeAbort() covers every
  // call site (bootstrap, bootstrapInfPlay, prefetch) with one change;
  // existing catch/finally blocks already treat abort as an ordinary
  // failure, so this only bounds worst-case duration, not behavior.
  // (BOOT_FETCH_TIMEOUT_MS is module-scoped — shared with the coalescer.)

  // -----------------------------------------------------------
  // Computed
  // -----------------------------------------------------------

  // Reuse the course index as playback advances; rebuild only when the map changes.
  const roundNumberByLego = computed(() => {
    const index = new Map<string, number>()
    for (const entry of roundMap.value?.rounds ?? []) {
      const legoId = entry.legoId
      if (!index.has(legoId)) index.set(legoId, entry.r)
    }
    return index
  })

  const currentRound: ComputedRef<number | null> = computed(() => {
    const lego = currentLegoId.value
    const map = roundMap.value
    if (!lego || !map) return null
    return roundNumberByLego.value.get(lego) ?? null
  })

  // -----------------------------------------------------------
  // Internals
  // -----------------------------------------------------------

  /**
   * `budgetMs` is how long THIS caller waits, not how long the request lives.
   * Critical-path callers pass CRITICAL_PATH_TIMEOUT_MS so the learner is
   * never held; background callers take the default long leash.
   */
  function makeAbort(budgetMs: number = BOOT_FETCH_TIMEOUT_MS): AbortController {
    const ctrl = new AbortController()
    activeAborts.add(ctrl)
    abortTimers.set(ctrl, setTimeout(() => ctrl.abort(), budgetMs))
    return ctrl
  }

  function releaseAbort(ctrl: AbortController): void {
    activeAborts.delete(ctrl)
    const timer = abortTimers.get(ctrl)
    if (timer) {
      clearTimeout(timer)
      abortTimers.delete(ctrl)
    }
  }

  /**
   * The bundle, but never for longer than the cold-start budget.
   *
   * `getCourseBundle` gives its own fetch 20 seconds — right for a one-time
   * multi-megabyte download, wrong for the boot path, which promises the
   * learner a first play in about two. Without this a flagged course on a
   * stalled connection would sit for 20s and only THEN fall through to
   * /round-map for another 2.5, which is not "the worst case is today's
   * behaviour" — it is four times worse than today.
   *
   * The download is not cancelled, and `getCourseBundle` de-dupes in flight,
   * so the very next page joins the same fetch and the session cuts over the
   * moment it lands. This bootstrap just refuses to wait for it.
   */
  async function bundleWithinBootBudget(code: string): Promise<CourseBundle> {
    let timer: ReturnType<typeof setTimeout> | undefined
    // ONE budget for the whole boot, not one per stage (job #926). The round
    // map and the cycles page each asked for the bundle in turn, so on a link
    // too slow for it they waited 8 s EACH (measured on Slow 3G: 9.2 s, then
    // 17.9 s) before the cheap endpoints were even asked. Once a stage has
    // given up, later stages take the budget at zero: an already-resolved
    // bundle still wins the race below, a pending one is not waited for again.
    //
    // And no wait at all when no copy of the bundle is on the device (job
    // #287, Tom: the first word needs the cursor and the first round's clips,
    // never a whole-course download). With nothing local the wait IS the
    // download, 2.3 MB on Spanish, and the /round-map + /cycles path the other
    // courses boot from is answering in under a second. The download keeps
    // going; later pages use the bundle the moment it lands.
    // Nor is the download STARTED before the first sound when there is no
    // copy: on a phone 2.3 MB shares the pipe with the first round's clips.
    // A copy on the device only counts when it is CURRENT: confirming that is
    // local (the boot's head line, or a head probe). A stale copy is never
    // refetched in front of the first sound (job #196, Tom 2026-10-22): the
    // first window plays off the cheap endpoints and the refresh follows it.
    const deferToFirstSound = (why: string): never => {
      bundleGivenUpForBoot = true
      if (!bundleAfterFirstSound.has(code)) {
        bundleAfterFirstSound.add(code)
        void firstSound().then(() => getCourseBundle(code)).catch(() => {})
      }
      throw Object.assign(new Error(`[InstantPlayback] ${why}; it loads after the first sound`), { bundleBudgetExceeded: true, bundleDeferred: true })
    }
    const beforeSound = !firstSoundFiredBy()
    // THE BOOT CONTRACT (Tom, 2026-10-29, job #290): open = the learner's
    // position from the database + the first cycle's audio, then play. The
    // whole-course bundle is never waited on before the first sound, not even
    // a copy already on the device: reading it back deserialises the whole
    // course (14 MB on Spanish), and on Tom's phone that read plus its
    // freshness check held the first window for the full 8 s budget. The
    // boot-answered /round-map + /cycles window plays instead; the bundle
    // loads behind it. Two exceptions, both free or forced: a copy already in
    // memory this session, and no network at all, where the copy on the
    // device is the only thing that can play.
    if (beforeSound && !hasSessionCourseBundle(code)) {
      if (!isOfflineish()) deferToFirstSound('the first sound never waits on the whole course')
      if (!(await hasLocalCourseBundle(code))) deferToFirstSound('no bundle on this device')
    }
    const budgetMs = bundleGivenUpForBoot ? 0 : BUNDLE_BOOT_BUDGET_MS
    try {
      return await Promise.race([
        (beforeSound ? getCourseBundle(code, { noDownload: true }) : getCourseBundle(code)).catch((err) => {
          if ((err as { bundleStale?: boolean })?.bundleStale) deferToFirstSound('local bundle is stale')
          throw err
        }),
        new Promise<never>((_resolve, reject) => {
          timer = setTimeout(
            () =>
              reject(
                Object.assign(
                  new Error(
                    `[InstantPlayback] bundle not ready inside the ${BUNDLE_BOOT_BUDGET_MS}ms boot budget`,
                  ),
                  { bundleBudgetExceeded: true },
                ),
              ),
            budgetMs,
          )
        }),
      ])
    } catch (err) {
      if ((err as { bundleBudgetExceeded?: boolean })?.bundleBudgetExceeded) bundleGivenUpForBoot = true
      throw err
    } finally {
      if (timer) clearTimeout(timer)
    }
  }

  /**
   * Every bundle consumer goes through here so that "did this session use the
   * bundle or fall back?" is a telemetry fact rather than a console line. The
   * fallback itself is unchanged and deliberate — the caller still catches and
   * takes the old network path — this only makes the choice observable.
   */
  async function fromBundle<T>(
    stage: BundlePathStage,
    code: string,
    project: (bundle: CourseBundle) => T,
  ): Promise<T> {
    const startedAt = Date.now()
    try {
      const out = project(await bundleWithinBootBudget(code))
      reportBundlePath({
        stage,
        outcome: 'bundle',
        waitedMs: Date.now() - startedAt,
        budgetMs: BUNDLE_BOOT_BUDGET_MS,
      })
      return out
    } catch (err) {
      reportBundlePath({
        stage,
        // A deliberate deferral (job #287: no current copy on the device, the
        // download follows the first sound) is its own outcome so the fallback
        // share counts only boots the bundle failed to serve. Labelling only:
        // the caller still takes the old endpoints exactly as before.
        outcome: (err as { bundleDeferred?: boolean })?.bundleDeferred ? 'deferred' : 'fallback',
        waitedMs: Date.now() - startedAt,
        budgetMs: BUNDLE_BOOT_BUDGET_MS,
        reason: (err as { bundleBudgetExceeded?: boolean })?.bundleBudgetExceeded
          ? 'budget'
          : (err as { bundlePreviewOnly?: boolean })?.bundlePreviewOnly
            ? 'preview'
            : 'error',
        detail: String((err as Error)?.message ?? err).slice(0, 200),
      })
      throw err
    }
  }

  async function fetchRoundMap(): Promise<RoundMap> {
    const code = courseCode.value
    if (!code) throw new Error('[InstantPlayback] courseCode is empty')

    // Bundle cutover: the round map is a field of the bundle, so a
    // bundle-enabled course never fetches /round-map at all. Any failure
    // falls through to the network path below.
    if (isBundleBootstrapEnabled(code)) {
      try {
        const map = await fromBundle('round_map', code, bundleToRoundMap)
        writeCachedRoundMap(code, map)
        return map
      } catch (err) {
        console.warn('[InstantPlayback] bundle round-map failed, falling back to /round-map:', err)
      }
    }

    // Cache hit — instant
    const cached = readCachedRoundMap(code)
    if (cached) {
      // We still fire a background revalidation to pick up version
      // bumps without blocking the cold path. Fire-and-forget; no
      // await, errors swallowed.
      void revalidateRoundMap(code, cached)
      return cached
    }

    // Cache miss — one tiny fetch (~20 KB for a 700-LEGO course). With no
    // cached map this device holds nothing for the course, so giving up early
    // plays nothing sooner: it only starts the legacy walk, ten times the
    // bytes, on the same slow pipe (job #773). Wait for the answer; fall back
    // on a real failure, or at the first-visit ceiling if the request hangs.
    const ctrl = makeAbort(criticalPathBudgetMs(false))
    try {
      const res = await coalescedJsonGet(
        `${apiBase}/${encodeURIComponent(code)}/round-map`,
        ctrl.signal,
      )
      if (!res.ok) {
        markNetworkStalled()
        throw Object.assign(
          new Error(`[InstantPlayback] round-map fetch failed: ${res.status} ${res.statusText}`),
          { status: res.status },
        )
      }
      clearNetworkStalled()
      const map = res.data as RoundMap
      writeCachedRoundMap(code, map)
      return map
    } catch (err) {
      markNetworkStalled()
      throw err
    } finally {
      releaseAbort(ctrl)
    }
  }

  /**
   * Background revalidation. Overwrite the cache when the server map differs
   * from the cached one — a higher version (normal content bump) OR a changed
   * round count at the same version (content edited without a clean version
   * bump, or a map that was cached while the source matview was still partial/
   * unrefreshed). The latter is what stranded a freshly-built course at "one
   * seed → INF PLAY". The fix lands on the NEXT cold start. Never throws.
   */
  async function revalidateRoundMap(code: string, cached: RoundMap): Promise<void> {
    try {
      const res = await coalescedJsonGet(`${apiBase}/${encodeURIComponent(code)}/round-map`)
      if (!res.ok) return
      const fresh = res.data as RoundMap
      const changed =
        fresh.version > cached.version ||
        (fresh.version === cached.version && fresh.rounds.length !== cached.rounds.length)
      if (changed) {
        writeCachedRoundMap(code, fresh)
        // Mirror into the live ref so consumers see the new version
        // immediately if they query roundMap.value afterward.
        if (roundMap.value && roundMap.value.course_code === code) {
          roundMap.value = fresh
        }
      }
    } catch {
      // Offline / network blip — keep the cached copy.
    }
  }

  async function fetchCycles(
    fromLegoId: string,
    limit: number,
    signal?: AbortSignal,
    expectedVersion?: number,
  ): Promise<CyclesResponse> {
    const code = courseCode.value
    if (!code) throw new Error('[InstantPlayback] courseCode is empty')

    // The admin CONTENT BLACKOUT test switch (playback/contentBlackout.ts) makes
    // the next new LEGO genuinely unreachable so PRACTISING mode can be watched
    // on a real device. It has to decline the bundle too: a bundle-enabled
    // course answers every cycles question from IndexedDB with no network at
    // all, so without this the switch would change nothing on the fifteen
    // courses that matter most. Off by default, in memory only, never persisted.
    const blackout = isContentBlackoutActive()

    // Bundle cutover: generate this page of cycles from the in-memory bundle
    // instead of fetching /cycles. Synchronous once the bundle is cached, so
    // resume on a bundle-enabled course is zero-network. Any failure falls
    // through to the network path below.
    if (isBundleBootstrapEnabled(code) && !blackout) {
      try {
        return await fromBundle('cycles', code, (bundle) =>
          bundleToCyclesResponse(bundle, fromLegoId, limit),
        )
      } catch (err) {
        console.warn('[InstantPlayback] bundle cycles failed, falling back to /cycles:', err)
      }
    }

    // Cache-first: if we know the expected version (i.e. the caller already
    // has the round-map and can validate), serve the cached cycles response
    // when it matches. Skip network entirely. This is the "zero-network
    // resume" path for returning visitors to a previously-fetched position.
    if (typeof expectedVersion === 'number' && !blackout) {
      const cached = readCachedCycles(code, fromLegoId, expectedVersion)
      if (cached) {
        // Only return cache if it covers >= the requested limit — a tiny
        // cached response (e.g. an old limit=1 entry) shouldn't satisfy a
        // full-round request.
        if (cached.cycles.length >= limit || cached.next_lego_id === null) {
          return cached
        }
      }
    }

    const url =
      `${apiBase}/${encodeURIComponent(code)}/cycles` +
      `?from=${encodeURIComponent(fromLegoId)}&limit=${limit}`

    // The network is now the SECOND choice, not the gate. Anything that goes
    // wrong from here — a hang the caller's signal aborts, a 403 from the
    // entitlement gate, a 5xx, a DNS failure in airplane mode — falls to
    // whatever cycles this device already holds for this position rather than
    // to a spinner. Only a genuinely empty cache produces an error, because
    // only then is there truly nothing to play. (Tom 2026-08-15: "Play what
    // you have. Verify access as and when you can. Never as a gate.")
    const serveStale = (why: string): CyclesResponse | null => {
      // Under the blackout this device is standing in for one that has never
      // held the next new LEGO — which is the real trigger condition, since the
      // whole job of that fetch is to reach content the learner has never
      // reached. Our own one-round-ahead prefetch happens to have cached the
      // very round tier 3 asks for, so consulting the cache here would answer
      // 'fetched' and the switch would look broken until the learner advanced a
      // round. Declining it makes the switch mean what it says.
      const stale = blackout ? null : readAnyCachedCycles(code, fromLegoId)
      if (stale) {
        console.warn(
          `[InstantPlayback] ${why} — serving ${stale.cycles.length} cached cycles for ${fromLegoId} instead of blocking the learner.`,
        )
      }
      return stale
    }

    let res: CoalescedJson
    try {
      // Blackout: fail the network leg the way a dead network fails it — a
      // transport error, no status. Thrown HERE and not earlier so that
      // everything a real outage would still do, still happens: the cache-first
      // read above already ran, and `serveStale` below still serves whatever
      // this device holds. Only content this device has never held comes back
      // empty, which is precisely the next new LEGO, which is the trigger.
      if (blackout) throw new TypeError('Failed to fetch (simulated content blackout)')
      res = await coalescedJsonGet(url, signal)
    } catch (err) {
      // Abort (our own budget expiring) or a transport failure. Both mean the
      // network did not deliver in time; neither means "stop the learner".
      markNetworkStalled()
      const stale = serveStale(
        (err as Error)?.name === 'AbortError'
          ? 'cycles fetch exceeded its critical-path budget'
          : `cycles fetch failed (${(err as Error)?.message ?? 'unknown'})`,
      )
      if (stale) return stale
      throw err
    }

    if (!res.ok) {
      const stale = serveStale(`cycles fetch returned ${res.status} ${res.statusText}`)
      if (stale) return stale
      throw Object.assign(
        new Error(`[InstantPlayback] cycles fetch failed: ${res.status} ${res.statusText}`),
        { status: res.status },
      )
    }
    clearNetworkStalled()
    const response = res.data as CyclesResponse
    // Write-through cache. Stamp is the version the server returned, which
    // gets compared against the current round-map version on read.
    writeCachedCycles(code, fromLegoId, response)
    return response
  }

  /**
   * LEGOs currently known to be PARTIAL — the API returned cycles for them
   * but stopped mid-LEGO (response.next_lego_id pointed back at them). We
   * must NOT emit a Round for these LEGOs yet: SimplePlayer's appendRounds
   * dedupes by roundNumber, so a partial Round becomes permanently stuck.
   * Wait until a follow-up fetch clears the partial flag (= a later response
   * either pushed cycles for the NEXT LEGO, or returned the partial LEGO's
   * remaining cycles with next_lego_id pointing past it).
   *
   * Cleared the instant a response includes ANY cycles for the LEGO without
   * setting next_lego_id back to it: that means the API has now emitted past
   * the partial point, so all of this LEGO's cycles are in the buffer.
   */
  const partialLegoIds = ref<Set<string>>(new Set())

  /**
   * Buffer cycles from a /cycles or /infplay-cycles response. The
   * `nextLegoId` arg is the response's pagination cursor: if non-null
   * AND it matches a LEGO that received cycles in this batch, that
   * LEGO is partial. Every other LEGO that received cycles is now
   * fully buffered (the API emits cycles in round-map order).
   */
  function bufferCycles(cycles: BackendCycle[], nextLegoId?: string | null): void {
    const buf = cycleBuffer.value
    const legosInBatch = new Set<string>()
    for (const cycle of cycles) {
      // Bucket by ROUND, not by subject. A spaced-review cycle is about an
      // earlier LEGO but plays in this round; keying it on `lego_id` would
      // file it back under a round the learner has already left (or has not
      // reached), and `backendCyclesToRounds` would never emit it.
      const roundKey = cycle.round_lego_id ?? cycle.lego_id
      legosInBatch.add(roundKey)
      const existing = buf.get(roundKey)
      if (!existing) {
        buf.set(roundKey, [cycle])
        continue
      }
      // Dedupe by cycle.id — re-fetching the same round is a no-op,
      // and we never want to silently double-count cycles in the
      // buffer (would make `next_lego_id` walking double-emit).
      if (!existing.some((c) => c.id === cycle.id)) {
        existing.push(cycle)
      }
    }
    // Update partial-LEGO bookkeeping. Every LEGO that received cycles
    // EXCEPT the partial tail (if any) is now complete.
    const partial = partialLegoIds.value
    let mutated = false
    for (const id of legosInBatch) {
      if (id === nextLegoId) {
        if (!partial.has(id)) { partial.add(id); mutated = true }
      } else {
        if (partial.delete(id)) mutated = true
      }
    }
    if (mutated) partialLegoIds.value = new Set(partial)
  }

  /**
   * True iff every cycle for `legoId` is in the buffer. False both for
   * LEGOs not yet fetched AND for LEGOs whose last fetch was a partial
   * tail. `backendCyclesToRounds` uses this to gate Round emission so a
   * partial LEGO never becomes a permanently-truncated Round.
   */
  function isLegoComplete(legoId: string): boolean {
    if (!cycleBuffer.value.has(legoId)) return false
    return !partialLegoIds.value.has(legoId)
  }

  // -----------------------------------------------------------
  // Public API: INF PLAY bootstrap — parallel to bootstrap() but
  // sources cycles from /api/courses/:code/infplay-cycles instead of
  // /cycles. Same instant-playback shape: ~150-300ms to first cycle,
  // background prefetch keeps the queue ahead of playback.
  //
  // Tom 2026-05-20: prior INF PLAY entry fell back to legacy
  // generateScript (5-15s on cold cache); this endpoint matches the
  // main-loop bootstrap latency.
  // -----------------------------------------------------------

  /** Pagination cursor for infplay round prefetch (1-based). */
  const nextInfRoundCursor = ref<number>(1)

  /** Cycles buffered from /infplay-cycles, walked by SimplePlayer in
   *  the order they were emitted (server returns spaced rep before
   *  random USE per round). Each cycle has inf_round on it. */
  const infPlayCycles = ref<BackendCycle[]>([])

  type InfPlayBatch = { cycles: BackendCycle[]; nextInfRound: number; mainLoopCount: number; version: number }

  /**
   * INF PLAY deliberately does NOT read from cache on the happy path — its
   * revival rounds are randomised per session, and serving the same sequence
   * every time would be pedagogically wrong (see the cache-fast-path note in
   * LearningPlayer). That reasoning holds only while the network is answering.
   * With no network, the choice collapses to "the same rounds again" vs
   * "nothing at all", and Tom's ruling settles it: play what you have. So we
   * write every batch through to storage and read it back ONLY as a
   * last resort, after the network has already failed. (Tom 2026-08-15.)
   */
  function infPlayCacheKey(code: string, fromRound: number): string {
    return `${CYCLES_STORAGE_PREFIX}infplay-${code}-${fromRound}`
  }

  function writeCachedInfPlay(code: string, fromRound: number, batch: InfPlayBatch): void {
    try {
      localStorage.setItem(infPlayCacheKey(code, fromRound), JSON.stringify(batch))
    } catch {
      /* storage full — a cache miss offline is no worse than today */
    }
  }

  function readCachedInfPlay(code: string, fromRound: number): InfPlayBatch | null {
    try {
      const raw = localStorage.getItem(infPlayCacheKey(code, fromRound))
      if (!raw) return null
      const parsed = JSON.parse(raw) as InfPlayBatch
      return Array.isArray(parsed?.cycles) && parsed.cycles.length > 0 ? parsed : null
    } catch {
      return null
    }
  }

  async function fetchInfPlayCycles(
    fromRound: number,
    limit: number,
    signal?: AbortSignal,
  ): Promise<InfPlayBatch> {
    const code = courseCode.value
    if (!code) throw new Error('[InstantPlayback] courseCode is empty')
    const url = `${apiBase}/${encodeURIComponent(code)}/infplay-cycles?from_round=${fromRound}&limit=${limit}`
    const serveStale = (why: string): InfPlayBatch | null => {
      const stale = readCachedInfPlay(code, fromRound)
      if (stale) {
        console.warn(
          `[InstantPlayback] ${why} — replaying ${stale.cycles.length} cached INF PLAY cycles rather than blocking the learner.`,
        )
      }
      return stale
    }
    // Bundle cutover step 5b: INF PLAY rounds come out of the in-memory
    // bundle instead of /infplay-cycles on a flagged course.
    //
    // A PREVIEW bundle is never used here. /infplay-cycles is the one content
    // endpoint with no preview slice — it hard-403s a non-entitled caller,
    // because INF PLAY is by definition past the free window — and generating
    // locally from a 19-seed preview bundle would hand that caller an INF PLAY
    // session the server just refused. So a previewOnly bundle falls straight
    // through to the network, which denies it, exactly as today.
    if (isBundleBootstrapEnabled(code)) {
      try {
        const batch = await fromBundle('infplay', code, (bundle): InfPlayBatch => {
          if (bundle.previewOnly) {
            // Deliberate pass-through to the network, which will deny it.
            // Thrown (not returned null) so it lands on fromBundle's fallback
            // report rather than being counted as a bundle-served round.
            throw Object.assign(new Error('preview bundle — INF PLAY not generated locally'), {
              bundlePreviewOnly: true,
            })
          }
          const json = bundleToInfPlayCyclesResponse(bundle, fromRound, limit)
          return {
            cycles: json.cycles,
            nextInfRound: json.next_inf_round,
            mainLoopCount: json.main_loop_count,
            version: json.version,
          }
        })
        writeCachedInfPlay(code, fromRound, batch)
        return batch
      } catch (err) {
        console.warn('[InstantPlayback] bundle INF PLAY failed, falling back to /infplay-cycles:', err)
      }
    }

    try {
      return await fetchInfPlayCyclesLive(url, code, fromRound, signal)
    } catch (err) {
      markNetworkStalled()
      const stale = serveStale(`INF PLAY fetch failed (${(err as Error)?.message ?? 'unknown'})`)
      if (stale) return stale
      throw err
    }
  }

  async function fetchInfPlayCyclesLive(
    url: string,
    code: string,
    fromRound: number,
    signal?: AbortSignal,
  ): Promise<InfPlayBatch> {
    // INF PLAY is even more strongly gated than /cycles — non-entitled callers
    // get a hard 403 (no partial-preview slice). Attach the session token so a
    // signed-in paid learner isn't seen as anonymous. (Same regression class as
    // /cycles above.)
    const headers = await authHeaders()
    const res = await fetch(url, headers ? { signal, headers } : { signal })
    if (!res.ok) {
      if (res.status === 403) {
        console.error(
          `[InstantPlayback] 403 on ${url} — entitlement gate denied INF PLAY; ` +
          `signed-in paid session token not reaching the server.`,
        )
      }
      throw Object.assign(
        new Error(`[InstantPlayback] infplay-cycles fetch failed: ${res.status}`),
        { status: res.status },
      )
    }
    const json = (await res.json()) as {
      course_code: string
      version: number
      cycles: BackendCycle[]
      next_inf_round: number
      main_loop_count: number
    }
    clearNetworkStalled()
    const batch: InfPlayBatch = {
      cycles: json.cycles,
      nextInfRound: json.next_inf_round,
      mainLoopCount: json.main_loop_count,
      version: json.version,
    }
    writeCachedInfPlay(code, fromRound, batch)
    return batch
  }

  /**
   * `fromRound` is the learner's CURRENT INF PLAY round (1-based;
   * round 1 = first round past main-loop). For fresh-into-infplay or
   * a learner who hasn't accumulated any infplay rounds yet, pass 1.
   * For a returning deep-infplay learner (e.g. infplay_round_index=
   * 50), pass 50 so spaced rep math computes correctly for absolute
   * round = mainLoopCount + 50, not + 1.
   */
  async function bootstrapInfPlay(fromRound: number = 1): Promise<BootstrapResult> {
    isReady.value = false
    nextInfRoundCursor.value = Math.max(1, fromRound)
    // Critical path — the learner is waiting behind this one too. With no
    // cached INF PLAY batch to fall back on, giving up early plays nothing
    // sooner: it starts the legacy whole-course walk (job #290, Tom's German:
    // /infplay-cycles answered in 2.52 s, the 2.5 s abort fired, and the walk
    // put the first sound at 23.6 s). Same rule as job #773's first visit.
    const code = courseCode.value
    const hasCachedBatch = !!code && readCachedInfPlay(code, nextInfRoundCursor.value) !== null
    const ctrl = makeAbort(criticalPathBudgetMs(hasCachedBatch))
    try {
      const result = await fetchInfPlayCycles(nextInfRoundCursor.value, INFPLAY_BOOT_ROUNDS, ctrl.signal)
      infPlayCycles.value = result.cycles
      nextInfRoundCursor.value = result.nextInfRound
      const firstCycle = result.cycles[0]
      if (!firstCycle) {
        throw new Error('[InstantPlayback] /infplay-cycles returned no cycles')
      }
      // Buffer into the standard map so backendCyclesToRounds can pick
      // them up. INF PLAY rounds don't have a round-map equivalent —
      // we synthesize one from the inf_round numbers below.
      bufferCycles(result.cycles)
      currentLegoId.value = firstCycle.lego_id
      isReady.value = true
      // Manufacture a "round-map" for backendCyclesToRounds. Each
      // unique inf_round becomes a synthetic round entry. legoId is
      // the FIRST cycle in that inf_round (purely cosmetic — keeps
      // adapter happy; cursor logic uses lastMainLoopLegoId anyway).
      const seenRounds = new Map<number, BackendCycle>()
      for (const c of result.cycles) {
        const r = c.inf_round
        if (typeof r === 'number' && !seenRounds.has(r)) seenRounds.set(r, c)
      }
      roundMap.value = {
        course_code: courseCode.value,
        version: result.version,
        rounds: [...seenRounds.entries()]
          .sort(([a], [b]) => a - b)
          .map(([r, c]) => ({
            r: result.mainLoopCount + r,  // absolute round number
            legoId: c.lego_id,
            seed: c.seed_number,
          })),
      }
      return { firstCycle, mapVersion: result.version }
    } finally {
      releaseAbort(ctrl)
    }
  }

  /** Background prefetch — fetches the next INF PLAY round window. */
  async function prefetchNextInfPlayBatch(): Promise<void> {
    const ctrl = makeAbort()
    try {
      const result = await fetchInfPlayCycles(
        nextInfRoundCursor.value,
        INFPLAY_ROUND_LIMIT,
        ctrl.signal,
      )
      if (result.cycles.length === 0) return
      bufferCycles(result.cycles)
      infPlayCycles.value = [...infPlayCycles.value, ...result.cycles]
      nextInfRoundCursor.value = result.nextInfRound
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        console.warn('[InstantPlayback] infplay prefetch failed:', err)
      }
    } finally {
      releaseAbort(ctrl)
    }
  }

  // -----------------------------------------------------------
  // Public API: bootstrap (the critical path)
  // -----------------------------------------------------------

  async function bootstrap(legoId?: string): Promise<BootstrapResult> {
    isReady.value = false

    // 1. Resolve starting legoId from the caller / position store. The
    //    resolver is responsible for translating "where the learner left
    //    off" into "what LEGO should we load now" — typically NEXT(last
    //    completed) via the round-map, so the resume LEGO is genuinely
    //    new to the learner and its intro plays naturally. Returning
    //    null = fresh learner; bootstrap falls to the round-map's first
    //    LEGO. Throwing = "no content available" (e.g. course end);
    //    bootstrap propagates and the caller falls to the legacy path.
    let startLegoId: string | null = legoId ?? null
    if (!startLegoId && resolveStartLegoId) {
      const resolved = await resolveStartLegoId()
      startLegoId = resolved ?? null
    }

    // 2. The bootstrap critical path: round-map fetch + first-round
    //    cycles fetch. These are INDEPENDENT when we already have a
    //    startLegoId — the cycles URL doesn't need the round-map to
    //    construct, just the LEGO. Run them in parallel via Promise.all
    //    so total wall time = max(map, cycles) instead of map+cycles.
    //    For a returning learner with cached round-map this is one
    //    cycles roundtrip and we're done.
    //
    //    If we DON'T have a startLegoId yet (fresh learner, empty
    //    position store), we have to wait for the round-map to find
    //    R1's legoId before the cycles fetch can fire. This is the
    //    one case where we're stuck serial — but it's rare (first ever
    //    visit, no progress) and even so it's only two roundtrips, the
    //    same as before.
    // Optimistic cache-hit path: if we have BOTH a known startLegoId AND a
    // cached round-map, the cycles cache can be checked with the expected
    // version before any network call fires. When both caches hit, this is
    // a zero-network resume.
    const cachedMap = startLegoId ? readCachedRoundMap(courseCode.value) : null
    const expectedVersion = cachedMap?.version

    // Critical path: the learner is waiting behind this. With something
    // cached for this course: 2.5 s, then cache. With nothing cached (a first
    // visit): wait for the fast path to answer, because the only fallback is
    // the far heavier legacy walk on the same pipe (job #773).
    // The bundle wait (at most BUNDLE_BOOT_BUDGET_MS, once) happens INSIDE this
    // signal's life on a bundle course, so the budget for the network fallback
    // is added to it. Without that, a device with a cached round map (2.5 s)
    // reached /cycles with the signal already aborted and fell to the legacy
    // walk, 3.7 MB on the slow pipe, even though /cycles answers in ~1 s (#926).
    const bundleWait = isBundleBootstrapEnabled(courseCode.value) ? BUNDLE_BOOT_BUDGET_MS : 0
    const ctrl = makeAbort(criticalPathBudgetMs(hasLocalCourseFallback(courseCode.value)) + bundleWait)
    let map: RoundMap
    let response: CyclesResponse
    try {
      if (startLegoId) {
        const [m, r] = await Promise.all([
          fetchRoundMap(),
          fetchCycles(startLegoId, BOOTSTRAP_LIMIT, ctrl.signal, expectedVersion),
        ])
        map = m
        response = r
      } else {
        map = await fetchRoundMap()
        const first = map.rounds[0]
        if (!first) {
          throw new Error('[InstantPlayback] round-map has no rounds')
        }
        startLegoId = first.legoId
        response = await fetchCycles(
          startLegoId,
          BOOTSTRAP_LIMIT,
          ctrl.signal,
          map.version,
        )
      }
    } finally {
      releaseAbort(ctrl)
    }

    roundMap.value = map

    const firstCycle = response.cycles[0]
    if (!firstCycle) {
      throw new Error(
        `[InstantPlayback] no cycles returned for legoId=${startLegoId}`,
      )
    }

    // Bootstrap returns the whole first round in `response.cycles`. The
    // buffer now holds intro+debut+builds+uses for the start LEGO, and
    // potentially the first cycles of the next LEGO too (if the round
    // had <BOOTSTRAP_LIMIT cycles). SimplePlayer can be initialised
    // with a complete round structure from this — no tier-1 await
    // needed before play.
    bufferCycles(response.cycles, response.next_lego_id)
    currentLegoId.value = firstCycle.lego_id
    isReady.value = true

    return { firstCycle, mapVersion: response.version }
  }

  // -----------------------------------------------------------
  // Public API: prefetch tiers
  // -----------------------------------------------------------

  /**
   * Tier 1 — rest of the current round.
   *
   * Walks forward from the current legoId, fetches the next ~15
   * cycles, and adds them to the buffer. Cancellable, error-swallow.
   * Never blocks playback.
   */
  async function prefetchTier1(): Promise<void> {
    const lego = currentLegoId.value
    if (!lego) return
    const ctrl = makeAbort()
    try {
      const response = await fetchCycles(
        lego,
        TIER_1_LIMIT,
        ctrl.signal,
        roundMap.value?.version,
      )
      bufferCycles(response.cycles, response.next_lego_id)
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        console.warn('[InstantPlayback] tier-1 prefetch failed:', err)
      }
    } finally {
      releaseAbort(ctrl)
    }
  }

  /**
   * Tier 3 — cycles for the NEXT round.
   *
   * The cycle metadata fetch (round queue continuation) stays — it
   * needs to land before round N+1 starts. 2026-05-23: dropped the
   * inline ~15-audio bulk prefetch that followed it (and the old
   * Tier 2 listening prefetch); JIT fetch + SW CacheFirst +
   * SimplePlayer.prefetchNextCycle cover the upcoming cycle's audio.
   *
   * 2026-08-04: that left ONE clip genuinely cold — the next round's INTRO
   * presentation. It is the longest clip in the course (~5.8s), it plays
   * first in the round, and `prefetchNextCycle`'s one-cycle lookahead can't
   * reach it across the round boundary until round N+1 is already in
   * `SimplePlayer.rounds`, which on the instant path it often isn't yet.
   * So it is warmed here, and ONLY it — a single fire-and-forget GET, not a
   * return of the bulk prefetch. Cache-as-you-go is unchanged.
   */
  async function prefetchTier3(): Promise<NextLegoFetchOutcome> {
    const map = roundMap.value
    const lego = currentLegoId.value
    if (!map || !lego) return 'skipped'

    const idx = map.rounds.findIndex((r) => r.legoId === lego)
    // THE LEARNER'S LEGO IS NOT IN THE MAP. This used to fall into the same
    // `no-next` as a genuine course end, which claims a fact about the CONTENT
    // ("there is nothing after this") off the back of a fact about OURSELVES
    // ("we cannot find where they are"). They are not the same thing, and the
    // difference matters the moment anyone tries to read the practising
    // telemetry afterwards: one is the course ending, the other is a cursor
    // that has drifted out of the map — a belt jump into a stale map, an
    // INF-PLAY round id, a course whose map was rebuilt underneath a live
    // session. Neither moves the mode, so nothing about behaviour changes;
    // 'skipped' simply stops the row lying about which one happened.
    if (idx < 0) return 'skipped'
    const next = map.rounds[idx + 1]
    // No round after this one is the COURSE'S OWN END, and the caller must be
    // able to tell that apart from a fetch that failed: one is a fact about
    // the content, the other is the trigger for PRACTISING mode.
    if (!next) return 'no-next'

    const ctrl = makeAbort()
    try {
      const response = await fetchCycles(
        next.legoId,
        TIER_3_LIMIT,
        ctrl.signal,
        map.version,
      )
      bufferCycles(response.cycles, response.next_lego_id)
      // Warm the next round's intro prompt — see the note above. The prompt
      // is `presentation || known`, mirroring `toPlayerCycle`, so a LEGO with
      // no presentation clip still gets its fallback clip warmed rather than
      // nothing. Everything else in the round still JIT-fetches at use time.
      warmIntroPrompt(response.cycles)
      return 'fetched'
    } catch (err) {
      // THE ONE TRIGGER (Tom, 2026-08-31). This is the fetch of the next NEW
      // LEGO — the one whose turn it is per the learner's cursor — and this
      // catch is the only place in the app that knows it came back with
      // nothing. The caller turns that into `playback/practisingMode`'s one
      // state change.
      //
      // `fetchCycles` does try this device's cached copy of that LEGO before
      // throwing, but do NOT read that as "failed means unreachable from
      // network AND cache" — an earlier comment here said exactly that and it
      // was wrong. The cached copy only exists if this same LEGO was fetched
      // successfully before, and the whole job of this fetch is to reach
      // content the learner has NEVER reached. For the next new LEGO that
      // cache is empty by definition, so effectively every throw arrives here.
      //
      // Which makes it our job to say what the throw was ABOUT.
      //
      //   • An abort is our own teardown or budget expiring — says nothing
      //     about anything.
      //   • 401/403 (a login that expired), 429 (our rate limit), 5xx (our
      //     server) are OUR outage, not the learner's connection. A single one
      //     of those used to freeze a paying learner's position for the rest of
      //     the session, with no button to force a re-check and no retry — and
      //     a Supabase or auth wobble would do it to everybody at once. Our
      //     failure must not cost the learner their progress, so these hold the
      //     mode where it is and the next probe simply tries again.
      //   • Anything else — transport failure, DNS, a 404 on the content — is
      //     the learner genuinely unable to reach the next new LEGO. That, and
      //     only that, is PRACTISING.
      if ((err as Error)?.name === 'AbortError') return 'skipped'
      const status = (err as { status?: number })?.status
      if (status === 401 || status === 403 || status === 429 || (typeof status === 'number' && status >= 500)) {
        console.warn(`[InstantPlayback] next-new-LEGO fetch got ${status} — our side, not the learner's; leaving the mode as it is`, err)
        return 'skipped'
      }
      console.warn('[InstantPlayback] next-new-LEGO fetch failed:', err)
      return 'failed'
    } finally {
      releaseAbort(ctrl)
    }
  }

  /**
   * The round BEFORE the current one (job #812). A cold bootstrap loads forward
   * from the resume point, so the first back / ‹‹ used to wait for the whole
   * course walk (generateScript). One extra round fetched after the first cycle
   * is already on its way makes that first step back instant; anything earlier
   * stays on-demand (job #783).
   *
   * Runs AFTER bootstrap resolves (never in front of the first cycle) and
   * buffers ONLY the previous round's own cycles: the response runs on into the
   * current round, and buffering that tail would mark the current LEGO partial
   * (`next_lego_id` points at it) and drop it from backendCyclesToRounds.
   * Outcomes: 'fetched', 'none' (course start — nothing is requested),
   * 'skipped' (cursor not in map, or the round came back incomplete), 'failed'.
   */
  // The clip warming the last prefetchPrevious started, so a deeper step waits for it rather than competing with it for a slow pipe.
  let previousWarm: Promise<void> = Promise.resolve()
  // Bumped by cancel(): a prefetch parked on previousWarm holds no controller to abort, so it compares this on waking instead.
  let cancelGeneration = 0

  // `stepsBack` 2 buffers the round before the previous one (job #973: the SECOND back-step measured 15 s on fast 3G and 30 s on slow 3G,
  // because only one round behind the cursor was ever buffered). It waits for the first round's clips so the one the learner will hear
  // first is never starved.
  async function prefetchPrevious(stepsBack = 1, settleMs = stepsBack > 1 ? SECOND_ROUND_SETTLE_MS : 0): Promise<'fetched' | 'none' | 'skipped' | 'failed'> {
    const map = roundMap.value
    const lego = currentLegoId.value
    if (!map || !lego) return 'skipped'
    const idx = map.rounds.findIndex((r) => r.legoId === lego)
    if (idx < 0) return 'skipped'
    if (idx - stepsBack < 0) return 'none'
    const prevId = map.rounds[idx - stepsBack].legoId
    if (stepsBack > 1) {
      const gen = cancelGeneration
      const code = courseCode.value
      await previousWarm
      // Measured on Slow 3G: fetching the second round straight after the first's clips slowed the FIRST back from 3.6 s to 9.9 s, so the
      // deeper round waits until the learner has had time to take that step.
      if (settleMs > 0) await new Promise((r) => setTimeout(r, settleMs))
      // The round id and map version above belong to the course that was playing before the wait.
      if (gen !== cancelGeneration || code !== courseCode.value || map !== roundMap.value) return 'skipped'
    }
    if (isLegoComplete(prevId) && getBufferedCyclesForLego(prevId).length > 0) return 'fetched'

    const ctrl = makeAbort()
    try {
      const response = await fetchCycles(prevId, TIER_1_LIMIT, ctrl.signal, map.version)
      // A round that fills the whole page has no end we can see — leave it to
      // the on-demand path rather than buffer a truncated one.
      if (response.next_lego_id === prevId) return 'skipped'
      const own = response.cycles.filter((c) => (c.round_lego_id ?? c.lego_id) === prevId)
      if (own.length === 0) return 'skipped'
      bufferCycles(own, null)
      // The cycles a back-step lands on are the round's first (round-back, the
      // intro, which plays its PRESENTATION clip, not the known one) and its last
      // (cycle-back). Warm both so the step is audible at once, not just visible
      // (job #890: round-back measured ~3 s on slow 3G with only the last warmed;
      // job #926: the intro's presentation clip was never asked for at all).
      // One clip at a time, first-heard first, the round's start before its end,
      // so a slow pipe finishes the clip the learner will hear before it starts
      // the next. Not awaited: the round is already buffered and playable.
      const heard = (c: BackendCycle) => [c.audio?.presentation_id, c.audio?.known_id, c.audio?.target1_id, c.audio?.target2_id]
      previousWarm = warmClipsInOrder([own[0], own[own.length - 1]].map(heard)).catch(() => {})
      return 'fetched'
    } catch (err) {
      if ((err as Error)?.name !== 'AbortError') {
        console.warn('[InstantPlayback] previous-round prefetch failed:', err)
      }
      return 'failed'
    } finally {
      releaseAbort(ctrl)
    }
  }

  // -----------------------------------------------------------
  // Public API: navigation + cleanup
  // -----------------------------------------------------------

  /**
   * Set the active legoId — called by the player layer after each
   * cycle/round transition so that `currentRound`, tier-1 and tier-3
   * resolve against the right anchor.
   */
  function setCurrentLegoId(legoId: string | null): void {
    currentLegoId.value = legoId
  }

  /**
   * Read cached cycles for a legoId — used by the player layer to
   * iterate through the round after `bootstrap()` returns. Returns
   * an empty array if no prefetch has populated this round yet.
   */
  function getBufferedCyclesForLego(legoId: string): BackendCycle[] {
    return cycleBuffer.value.get(legoId) ?? []
  }

  /**
   * Abort every in-flight fetch — call this on course switch /
   * unmount so we don't keep loading data the user isn't going to
   * consume.
   */
  function cancel(): void {
    cancelGeneration++
    for (const ctrl of activeAborts) {
      try {
        ctrl.abort()
      } catch {
        // Swallow — `AbortController.abort()` should never throw,
        // but be defensive about exotic environments.
      }
    }
    activeAborts.clear()
  }

  /** Drop in-memory state. Round-map localStorage cache is preserved. */
  function reset(): void {
    cancel()
    roundMap.value = null
    cycleBuffer.value = new Map()
    partialLegoIds.value = new Set()
    currentLegoId.value = null
    isReady.value = false
  }

  return {
    // First-paint critical path
    bootstrap,
    bootstrapInfPlay,
    prefetchNextInfPlayBatch,
    infPlayCycles,
    nextInfRoundCursor,

    // Prefetch tiers
    prefetchTier1,
    prefetchTier3,
    prefetchPrevious,

    // State
    currentRound,
    roundMap,
    isReady,

    // Resolver helpers — the caller's resolveStartLegoId needs the
    // round-map to compute NEXT(lastCompleted). Cached path is instant;
    // uncached path is one tiny fetch (~20 KB).
    getOrFetchRoundMap: fetchRoundMap,

    // Navigation helpers (player layer uses these to keep the
    // composable in sync with playback position).
    setCurrentLegoId,
    // Read-only for telemetry: this is the exact anchor `prefetchTier3` walks
    // the round-map from, so a practising row that carries it can be checked
    // against the map afterwards — which is how "the cursor was not in the
    // map" gets told apart from "the course genuinely ended".
    currentLegoId,
    getBufferedCyclesForLego,
    isLegoComplete,

    // Lifecycle
    cancel,
    reset,
  }
}

export type InstantPlayback = ReturnType<typeof useInstantPlayback>
